import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ScrollText,
  HandHeart,
  Users,
  ShieldAlert,
  UserCheck,
  Ban,
  ImageIcon,
  AlertTriangle,
  Compass,
  MessageCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Logo } from '@/components/Header';
import { openSupportEmail } from '@/lib/support';

interface SectionProps {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}

const Section: React.FC<SectionProps> = ({ icon: Icon, title, children }) => (
  <section className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm">
    <div className="flex items-start gap-4 mb-4">
      <div className="flex-shrink-0 w-11 h-11 rounded-full bg-gradient-to-br from-fuchsia-100 to-pink-100 flex items-center justify-center">
        <Icon className="w-5 h-5 text-fuchsia-600" />
      </div>
      <h2 className="font-serif text-xl sm:text-2xl font-bold text-slate-900 leading-tight pt-1.5">
        {title}
      </h2>
    </div>
    <div className="prose prose-slate max-w-none text-slate-700 text-[15px] leading-relaxed space-y-3 sm:pl-[60px]">
      {children}
    </div>
  </section>
);

const Terms: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50 flex flex-col">
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-slate-100 shadow-sm">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <button onClick={() => navigate('/')} aria-label="KinsCircle home">
            <Logo />
          </button>
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="rounded-full gap-1.5"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
        </div>
      </header>

      <main className="flex-1 max-w-3xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Hero */}
        <div className="text-center mb-10 sm:mb-14">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-fuchsia-50 border border-fuchsia-200 text-fuchsia-700 text-xs font-semibold mb-5">
            <ScrollText className="w-3.5 h-3.5" />
            Terms of Service
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-slate-900 leading-tight mb-4">
            The simple rules that
            <br />
            <span className="bg-gradient-to-r from-fuchsia-600 to-pink-500 bg-clip-text text-transparent">
              keep every circle kind.
            </span>
          </h1>
          <p className="text-slate-600 text-base sm:text-lg max-w-xl mx-auto">
            By using KinsCircle, you agree to use the platform responsibly and
            respectfully. These are the terms that hold our family circles together.
          </p>
          <p className="text-xs text-slate-400 mt-4">
            Last updated April 26, 2026
          </p>
        </div>

        <div className="space-y-5 sm:space-y-6">
          <Section icon={HandHeart} title="Using KinsCircle in good faith">
            <p>
              By creating an account or using KinsCircle in any way, you agree to these
              Terms of Service. If you do not agree, please do not use the platform.
            </p>
            <p>
              KinsCircle is a private space for families to remember, connect, and grow
              together. Every member is expected to participate in good faith and treat
              the circle with the same care they would treat their own home.
            </p>
          </Section>

          <Section icon={Users} title="Adding family, relationships &amp; content">
            <p>
              Members may add family members, relationships, photos, videos, life events,
              stories, and comments to their family circle. All contributions should be:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-fuchsia-500">
              <li>Made in good faith and as accurately as you are able.</li>
              <li>Shared with appropriate permission from the people involved.</li>
              <li>Limited to your own family network — not strangers or public figures.</li>
            </ul>
          </Section>

          <Section icon={Compass} title="A shared family platform">
            <p>
              KinsCircle is a <strong>shared</strong> family platform. The information in
              your circle — names, dates, relationships, photos, and stories — may be
              added or edited by multiple members of your family.
            </p>
            <p>
              Because of this, content on KinsCircle may not always be fully accurate or
              complete. If something is wrong, work with your family members to correct
              it, or reach out to support if you need help resolving a dispute.
            </p>
          </Section>

          <Section icon={UserCheck} title="Your responsibilities as a member">
            <p>
              KinsCircle works because every member helps protect the circle. As a
              member, you are responsible for:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-fuchsia-500">
              <li>
                <strong>Maintaining account security</strong> — using a strong, unique
                password and keeping your sign-in details private.
              </li>
              <li>
                <strong>Respecting family members' privacy</strong> — not sharing
                another relative's photos, stories, or details outside the circle
                without their permission.
              </li>
              <li>
                <strong>Avoiding harmful content</strong> — no harassment, abuse,
                threats, hate speech, harmful misinformation, or content that targets a
                family member or anyone else.
              </li>
            </ul>
          </Section>

          <Section icon={Ban} title="Removal &amp; account suspension">
            <p>
              KinsCircle may remove content, restrict features, or suspend or terminate
              accounts that violate these terms. Examples include:
            </p>
            <ul className="list-disc pl-5 space-y-1.5 marker:text-fuchsia-500">
              <li>Harassing, abusing, or threatening another member.</li>
              <li>Uploading content you do not have the right to share.</li>
              <li>Misleading or impersonating relatives, or creating fake profiles.</li>
              <li>Attempting to access circles, data, or accounts that aren't yours.</li>
            </ul>
            <p>
              We act on reports as quickly as we can and will let you know when an
              account-level action affects your access.
            </p>
          </Section>

          <Section icon={ImageIcon} title="Your content stays yours">
            <p>
              All content you upload to KinsCircle — photos, videos, stories, comments,
              and other contributions — <strong>remains owned by you</strong>.
            </p>
            <p>
              By uploading, you grant KinsCircle a limited license to store, process, and
              display that content within your family network so we can actually provide
              the service (showing your photos in the timeline, displaying your tree to
              invited relatives, etc.). We do not sell your content, and we do not use
              your family photos to train AI models.
            </p>
          </Section>

          <Section icon={ShieldAlert} title="Provided “as is”">
            <p>
              KinsCircle is provided <strong>“as is”</strong> and{' '}
              <strong>“as available,”</strong> without warranties of any kind — express or
              implied — including accuracy, fitness for a particular purpose,
              availability, or uninterrupted service.
            </p>
            <p>
              We work hard to keep the platform reliable, but we cannot guarantee that
              every feature will be available at all times, that data added by relatives
              is correct, or that the service will be free of errors or interruptions.
            </p>
          </Section>

          <Section icon={AlertTriangle} title="Use at your own discretion">
            <p>
              Use of KinsCircle is at your own discretion and risk. You are responsible
              for the relatives you invite, the information you share, and how you
              interact within your circle.
            </p>
            <p>
              To the maximum extent allowed by law, KinsCircle is not liable for indirect,
              incidental, or consequential losses arising from your use of the platform.
            </p>
          </Section>

          <Section icon={MessageCircle} title="Questions or reports">
            <p>
              If you have questions about these terms, want to report a violation, or
              need help with your account, our team is here for you. We respond to every
              message personally.
            </p>
            <div className="pt-1">
              <Button
                onClick={openSupportEmail}
                className="rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white gap-2"
              >
                <MessageCircle className="w-4 h-4" />
                Contact Support
              </Button>
            </div>
          </Section>
        </div>

        <div className="mt-10 sm:mt-14 text-center text-sm text-slate-500">
          <p>
            These Terms work alongside our{' '}
            <button
              onClick={() => navigate('/privacy')}
              className="text-fuchsia-600 hover:text-fuchsia-700 font-semibold underline-offset-2 hover:underline"
            >
              Privacy &amp; Safety
            </button>{' '}
            page, which explains how your family data is protected.
          </p>
          <p className="mt-3 italic text-slate-400">
            
          </p>
        </div>
      </main>

      
    </div>
  );
};

export default Terms;
